import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import * as Sentry from '@sentry/nextjs';
import { type ToastPosition, ToastType, useToasts } from '@hub/hooks/useToasts';
import { IbhFileType } from '@igus/iglidur-bearing-hub-api/src/data-contracts';

interface UseFileDownloadProps {
  fileName: string;
  appType: 'IBH' | 'FFH';
}

export enum FileExtension {
  PDF = 'pdf',
  STP = 'stp',
  STL = 'stl',
  ZIP = 'zip',
}

export const useFileDownload = ({ fileName, appType }: UseFileDownloadProps) => {
  const [loadingDownloadItem, setLoadingDownloadItem] = useState<Set<number>>(new Set());
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const handleFileLoadingState = (index: number, action: 'add' | 'delete') => {
    setLoadingDownloadItem(prev => {
      const reports = new Set(prev);
      if (action === 'add') {
        reports.add(index);
      } else if (action === 'delete') {
        reports.delete(index);
      }
      return reports;
    });
  };

  const getFileType = (fileLinkType: IbhFileType | 'sheet'): string => {
    switch (fileLinkType) {
      case IbhFileType.Stl:
      case IbhFileType.Stp:
        return 'model';
      case IbhFileType.Drawing:
        return 'drawing';
      case IbhFileType.Report:
        return 'report';
      case 'sheet':
        return 'data-sheet';
      default:
        return '';
    }
  };

  const generatePdfTimeStamp = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hour = date.getHours();
    const minute = date.getMinutes();
    return `${year}${month}${day}_${hour}${minute}`;
  };

  const handleAutoDownload = (
    blob: Blob,
    filename: string,
    fileExtension: FileExtension,
    index: number,
    open = false
  ) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.style.display = 'none';
    if (open) {
      link.setAttribute('target', '_blank');
    }
    link.setAttribute('download', `${filename}.${fileExtension}`);
    link.setAttribute('href', blobUrl);
    link.click();
    URL.revokeObjectURL(blobUrl);
    handleFileLoadingState(index, 'delete');
  };

  const downloadErrorHandler = (error: unknown, index: number) => {
    handleFileLoadingState(index, 'delete');
    addToast({
      content: t('NETWORK_FAILURE', {
        ns: appType === 'FFH' ? 'free-form-hub/freeFormHubCommon' : 'bearing-hub/bearingHub',
      }),
      type: ToastType.ERROR,
      position: 'top-72 right-0 items-center max-w-[45rem]' as ToastPosition,
    });
    Sentry.captureException(error, {
      extra: {
        message: 'Could not download file',
      },
    });
  };

  const downloadFileFromLink = (
    url: string,
    fileLinkType: IbhFileType | 'sheet',
    fileExtension: FileExtension,
    index: number,
    open = false
  ): void => {
    handleFileLoadingState(index, 'add');
    const filename = `${appType.toLowerCase()}_${fileName}_${generatePdfTimeStamp()}_${getFileType(fileLinkType)}`;
    const downloadLink = `${url}?filename=${filename}`;

    fetch(downloadLink)
      .then(response => response.blob())
      .then(blob => {
        handleAutoDownload(blob, fileName, fileExtension, index, open);
      })
      .catch(error => {
        downloadErrorHandler(error, index);
      });
  };

  const downloadFileFromBlob = (blob: Blob, filename: string, fileExtension: FileExtension, index: number): void => {
    handleAutoDownload(blob, filename, fileExtension, index);
  };

  return {
    loadingDownloadItem,
    downloadFileFromLink,
    downloadErrorHandler,
    setLoadingDownloadItem,
    downloadFileFromBlob,
  };
};
